/**
 * Runtime v3 — the query specification and its server-resolved period.
 *
 * One parameterized shape covers every read: measures, a period form, filters
 * by dimension and member ref, a grouping, a comparison basis, an ordering, a
 * limit and a budget. There is no per-question spec and no per-metric intent —
 * a new question is a new combination of these parameters, not a new tool.
 *
 * Relative period forms ("last 4 quarters versus the prior 4") are declared
 * here and RESOLVED ON THE SERVER against `asOf`. A caller never sends a
 * window it computed itself, because the window a model computes is a number
 * it invented.
 */

import type { IsoDate, IsoInstant, RuntimeV3PeriodGrain, RuntimeV3SchemaVersion } from './common';
import type { RuntimeV3AnalyticalRank } from './analytical-request';

export const RUNTIME_V3_PERIOD_FORMS = [
  /** One figure for the whole span. */
  'total',
  /** One figure per bucket across the span. */
  'by_period',
  /** The most recent bucket that carries rows. */
  'latest',
  /** The last N buckets, and — when a comparison is asked for — the N before them. */
  'last_n',
  /** An explicit start and end, both supplied by the caller. */
  'explicit_range',
] as const;
export type RuntimeV3PeriodForm = (typeof RUNTIME_V3_PERIOD_FORMS)[number];

/**
 * The period as REQUESTED. `start`/`end` are meaningful only for
 * `explicit_range`; `n` only for `last_n`. `asOf` is server-authored and is
 * what every relative form resolves against.
 */
export type RuntimeV3QueryPeriod = Readonly<{
  form: RuntimeV3PeriodForm;
  grain: RuntimeV3PeriodGrain;
  asOf: IsoInstant;
  n?: number;
  start?: IsoDate;
  end?: IsoDate;
}>;

/** One concrete bucket window, with the label an answer states it under. */
export type RuntimeV3ResolvedWindow = Readonly<{
  start: IsoDate;
  end: IsoDate;
  label: string;
}>;

/**
 * The period as RESOLVED. This is what an accepted plan carries and what a
 * replay re-executes: the same saved windows, not the same relative phrase
 * re-resolved against a later clock.
 */
export type RuntimeV3ResolvedPeriod = Readonly<{
  form: RuntimeV3PeriodForm;
  grain: RuntimeV3PeriodGrain;
  asOf: IsoInstant;
  windows: readonly RuntimeV3ResolvedWindow[];
  comparisonWindows?: readonly RuntimeV3ResolvedWindow[];
}>;

export const RUNTIME_V3_FILTER_OPERATORS = ['in', 'not_in'] as const;
export type RuntimeV3FilterOperator = (typeof RUNTIME_V3_FILTER_OPERATORS)[number];

/**
 * A filter names a dimension and its members BY REF.
 *
 * Never by label and never by a fragment of the request: a member ref is
 * either a literal span the caller supplied or a ref `discover` returned. That
 * is the argument rule, expressed in the type.
 */
export type RuntimeV3QueryFilter = Readonly<{
  dimensionRef: string;
  operator: RuntimeV3FilterOperator;
  memberRefs: readonly string[];
}>;

export const RUNTIME_V3_COMPARE_BASES = [
  'none',
  'prior_period',
  'prior_year',
  'explicit_window',
] as const;
export type RuntimeV3CompareBasis = (typeof RUNTIME_V3_COMPARE_BASES)[number];

export type RuntimeV3QueryCompare = Readonly<{
  basis: RuntimeV3CompareBasis;
  /** Required when `basis` is `explicit_window`; ignored otherwise. */
  window?: Readonly<{ start: IsoDate; end: IsoDate }>;
}>;

export const RUNTIME_V3_ORDER_DIRECTIONS = ['asc', 'desc'] as const;
export type RuntimeV3OrderDirection = (typeof RUNTIME_V3_ORDER_DIRECTIONS)[number];

export const RUNTIME_V3_ORDER_KEY_KINDS = ['measure', 'dimension'] as const;
export type RuntimeV3OrderKeyKind = (typeof RUNTIME_V3_ORDER_KEY_KINDS)[number];

export type RuntimeV3OrderKey =
  | Readonly<{ kind: 'measure'; ref: string }>
  | Readonly<{ kind: 'dimension'; ref: string }>;

export type RuntimeV3QueryOrderBy = Readonly<{
  key: RuntimeV3OrderKey;
  direction: RuntimeV3OrderDirection;
  tiePolicy?: RuntimeV3AnalyticalRank['tiePolicy'];
}>;

/**
 * The ceiling a plan must fit under. Exceeding it is a typed refusal with a
 * counter-proposal that narrows the request — never a truncated answer
 * presented as a complete one.
 */
export type RuntimeV3QueryBudget = Readonly<{
  maxRows: number;
  maxGroups: number;
  maxCells: number;
}>;

export const RUNTIME_V3_DEFAULT_QUERY_BUDGET: RuntimeV3QueryBudget = Object.freeze({
  maxRows: 200_000,
  maxGroups: 5_000,
  maxCells: 50_000,
});

/**
 * THE ONE READ SHAPE.
 *
 * `revisionId` is pinned by the server before execution. A spec that still
 * names a dataset without a revision has not been accepted yet.
 */
export type RuntimeV3QuerySpec = Readonly<{
  schemaVersion: RuntimeV3SchemaVersion;
  tenantId: string;
  datasetRef: string;
  revisionId: string;
  measureRefs: readonly string[];
  period: RuntimeV3QueryPeriod;
  filters: readonly RuntimeV3QueryFilter[];
  groupBy: readonly string[];
  compare: RuntimeV3QueryCompare;
  orderBy?: RuntimeV3QueryOrderBy;
  limit?: number;
  budget: RuntimeV3QueryBudget;
}>;

/**
 * The accepted, fully resolved form of a `QuerySpec`: the period is concrete
 * and the revision is pinned. This is the object a run stores and a replay
 * re-executes without the model.
 */
export type RuntimeV3ResolvedQuerySpec = Readonly<
  Omit<RuntimeV3QuerySpec, 'period'> & { period: RuntimeV3ResolvedPeriod }
>;
